import { useResizeObserver } from '@react-aria/utils';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './Body.module.scss';

type ModalContentBodyProps = {
  /** The children to render inside the component. */
  children: React.ReactNode;
  /** Optional property for overriding styles. */
  className?: string;
  /** Optional property for overriding styles. */
  style?: React.CSSProperties;
  /** Sets the horizontal alignment of the text. */
  textAlign?: 'start' | 'end' | 'justify' | 'center';
};

/**
 * The dialog's main content.
 */
export const ModalDialogBody = ({
  children,
  className,
  style,
  textAlign = 'start',
}: ModalContentBodyProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  // This state is used to determine whether or not to fade content at the bottom (when content is long and scrolls).
  const [scrollable, setScrollable] = useState(false);
  const [atBottom, setAtBottom] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const isScrollable = el.scrollHeight > el.clientHeight;
    setScrollable(isScrollable);
    if (isScrollable) {
      setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
    } else {
      setAtBottom(false);
    }
  }, []);

  // Re-detect scrollability when content height changes (e.g. accordions, lazy-loaded content).
  useResizeObserver({ ref: scrollRef, onResize: updateScrollState });

  // Keep atBottom in sync on scroll so the bottom fade hides when user reaches the end.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    const handleScroll = () => {
      if (!scrollRef.current) return;
      const target = scrollRef.current;
      setAtBottom(
        target.scrollTop + target.clientHeight >= target.scrollHeight - 1
      );
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [updateScrollState]);

  return (
    <div
      className={clsx(styles.modalDialogBody, className)}
      data-at-bottom={atBottom ? 'true' : undefined}
      data-scrollable={scrollable ? 'true' : undefined}
      data-text-align={textAlign}
      data-zds
      style={style}
    >
      <div className={styles.modalDialogBodyInner} data-zds ref={scrollRef}>
        {children}
      </div>
    </div>
  );
};
